import React, { ChangeEvent, useState } from 'react';
import InputLabel from '../inputLabel/InputLabel';
import TextInput from '../textInput/TextInput';

type SegmentInputValue = {
    value: string;
    key: string;
    maxLength?: number; // overrides SegmentedTextInputProps value
    placeholder?: string; // overrides SegmentedTextInputProps value
    maxLengthWidth?: boolean; // overrides SegmentedTextInputProps value
    numeric?: boolean; // overrides SegmentedTextInputProps value
};

export type SegmentedTextInputProps = {
    name: string;
    dataKey?: string;
    className?: string;
    placeholder?: string;
    label?: string;
    values?: SegmentInputValue[];
    maxLength?: number;
    maxLengthWidth?: boolean;
    numeric?: boolean;
    floatingLabel?: boolean;
    ariaDescribeLink?: string;
    onChange?: (value: SegmentInputValue[], key?: string) => void;
};

const SegmentedTextInput = ({
    name,
    dataKey = '',
    className,
    placeholder,
    label,
    values,
    maxLength,
    maxLengthWidth,
    numeric,
    floatingLabel,
    ariaDescribeLink,
    onChange
}: SegmentedTextInputProps) => {
    const [focusedInputIndex, setFocusedInputIndex] = useState(null);

    const handleInputsNavigation = ({
        newInputValues,
        changingInput,
        inputKey
    }: {
        newInputValues: SegmentInputValue[];
        changingInput: SegmentInputValue;
        inputKey: string;
    }) => {
        const index = newInputValues.findIndex((v) => v.key === inputKey);
        const textMaxLength = changingInput.maxLength ?? maxLength;

        if (!textMaxLength) {
            return;
        }

        if (
            changingInput.value.length === textMaxLength &&
            index < newInputValues.length - 1
        ) {
            setFocusedInputIndex(index + 1);
        }
    };

    const onInputsChange = (e: ChangeEvent<HTMLInputElement>, inputKey) => {
        const newValue = e.currentTarget.value;
        const newInputValues = [...values];
        const changingInput = newInputValues.find(
            ({ key }) => key === inputKey
        );
        if (changingInput.value === newValue) {
            return;
        }

        changingInput.value = newValue;

        handleInputsNavigation({ changingInput, inputKey, newInputValues });

        onChange?.(newInputValues, dataKey);
    };

    return (
        <div className={className}>
            {label && <InputLabel labelText={label} name={name} />}
            <div className='d-flex gap-1 flex-wrap'>
                {values.map(
                    (
                        {
                            key,
                            value,
                            placeholder: pl,
                            maxLength: ml,
                            maxLengthWidth: mlw,
                            numeric: num
                        },
                        i
                    ) => (
                        <TextInput
                            name={key}
                            key={key}
                            dataKey={key}
                            value={value}
                            placeholder={pl || placeholder}
                            maxLength={ml || maxLength}
                            maxLengthWidth={mlw || maxLengthWidth}
                            floatingLabel={floatingLabel}
                            focus={focusedInputIndex === i}
                            ariaDescribeLink={ariaDescribeLink}
                            numeric={num || numeric}
                            onChange={onInputsChange}
                            onLeave={() => setFocusedInputIndex(null)}
                        />
                    )
                )}
            </div>
        </div>
    );
};

export default SegmentedTextInput;
